var React = require('react');
var { connect } = require('react-redux');
var GraphToggle = require('GraphToggle');
const { useTranslation, withTranslation } = require('react-i18next');
var SensorGraph = require('SensorGraph');
const { useEffect, useState, useMemo } = require('react');
var GeneralClasses = require('GeneralClasses');
var { unitDisplayName } = require('unitsManager');
var dayjs = require('../../utils/dayjsConfig');
var {
  soilTempDefaultDaysBack,
  selectSoilTempSamplePoints,
  shouldHalveSoilTempSamples,
  halveSoilTempSamples,
} = require('./soilTemperatureGraphHelpers');
require('./AllFarmView/FieldDetailsWPGraph.css');

const DATE_FILTER_OPTIONS = [
  { days: 1, key: 'last_day' },
  { days: 7, key: 'last_week' },
  { days: 14, key: 'last_two_weeks' },
  { days: 30, key: 'last_month' },
  { days: 182, key: 'last_six_months' },
  { days: 365, key: 'last_year' },
  { days: 0, key: 'max' },
];

const SoilTemperatureGraphContainer = (props) => {
  const { t } = useTranslation();
  const [sensor_graph, setSensor_graph] = useState(null);
  // An archived field is past its season, so its newest soil-temp sample is
  // almost always older than the 7-day active default — with that default the
  // range filter dropped every sample and the graph painted nothing (and the
  // range dropdown is hidden in archive mode, so it couldn't be widened from
  // the UI). Archive therefore opens on the full history ("Max"), the same way
  // the water-potential graphs do.
  const [daysBack, setDaysBack] = useState(() =>
    soilTempDefaultDaysBack(props.field_state),
  );

  let userUnits =
    props.userData && 'temperature' in props.userData
      ? unitDisplayName(props.userData.temperature)
      : '';

  const soilTempValues =
    props.selectedField?.historical_data?.soil_temp?.values;
  const timezone = props.selectedField?.geo_data?.time_zone;

  let dataInLoadingState = soilTempValues;

  let graphTitleColor =
    props.field_state === 'active' ? 'text-grey45' : 'text-grey71';

  const dateFilterNames = useMemo(
    () =>
      DATE_FILTER_OPTIONS.reduce(
        (acc, opt) => ({
          ...acc,
          [opt.days]: t(opt.key),
        }),
        {},
      ),
    [t],
  );

  // Archive mode is entered/left by toggling the field-state switch. Today
  // DashboardContent renders separate archive/active subtrees (so this also
  // remounts the container), but reset the range explicitly so the default
  // can't be left over from the other mode.
  useEffect(() => {
    setDaysBack(soilTempDefaultDaysBack(props.field_state));
  }, [props.field_state]);

  const samples = useMemo(() => {
    if (!soilTempValues || Object.keys(soilTempValues).length === 0) {
      return [];
    }

    // Cutoff instant for the selected range; 0 (the "Max" option) means no
    // lower bound. Uses the field's timezone so the boundary lands on the
    // field's own clock.
    const cutoffTimestamp =
      daysBack > 0 ? dayjs().tz(timezone).subtract(daysBack, 'day').unix() : 0;

    let points = selectSoilTempSamplePoints(soilTempValues, cutoffTimestamp);
    if (shouldHalveSoilTempSamples(daysBack)) {
      points = halveSoilTempSamples(points);
    }

    return points.map((point, index) => ({
      date: timezone
        ? dayjs.unix(point.timestamp).tz(timezone)
        : dayjs.unix(point.timestamp),
      value: point.value,
      index,
    }));
  }, [soilTempValues, timezone, daysBack]);

  // Load data and reload graph when dependencies change
  useEffect(() => {
    if (!sensor_graph) {
      return;
    }

    if (samples.length > 0) {
      reloadGraph(samples);
      return;
    }

    // History arrived but nothing falls in range (an archived field whose
    // sensor never reported, say). Clear instead of leaving the previously
    // selected field's curve on screen.
    if (soilTempValues) {
      clearGraph();
    }
  }, [samples, soilTempValues, sensor_graph]);

  const handleTimeRangeChange = (e, days) => {
    e.preventDefault();
    // Don't stopPropagation — Bootstrap relies on the click bubbling up
    // to auto-close the dropdown menu after selection.
    setDaysBack(days);
    mixpanel.track('Soil Temperature Timeframe Selection Changed', {
      'Days Back': days,
    });
  };

  const clearGraph = () => {
    if (!sensor_graph) {
      return;
    }
    sensor_graph.loadData(null, null, undefined, undefined, {}, {});
  };

  const reloadGraph = (samples) => {
    if (!samples || samples.length === 0 || !sensor_graph) {
      return;
    }

    const dataToDisplay = {
      soil_temp: new GeneralClasses.Sensor(
        {
          id: t('soil_temperature'),
          name: t('soil_temperature'),
          displayName: t('soil_temperature'),
          data: samples,
          color: '#20a2cf',
          coordinates: {
            latitude: props.selectedField?.geo_data?.latitude || '0',
            longitude: props.selectedField?.geo_data?.longitude || '0',
          },
          settings: { is_active: true },
        },
        t('soil_temperature'),
      ),
    };

    const historicalData = props.selectedField?.historical_data;

    sensor_graph.loadData(
      dataToDisplay,
      historicalData,
      undefined,
      timezone,
      {},
      {},
    );

    // Set Y-axis bounds based on temperature data
    if (sensor_graph.current_chart) {
      const values = samples.map((s) => s.value);
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      const padding = (maxVal - minVal) * 0.1 || 5;

      sensor_graph.current_chart.options.scales.y.min = minVal - padding;
      sensor_graph.current_chart.options.scales.y.max = maxVal + padding;
      delete sensor_graph.current_chart.options.scales.y.suggestedMin;
      delete sensor_graph.current_chart.options.scales.y.suggestedMax;
      sensor_graph.current_chart.options.scales.y.beginAtZero = false;
      sensor_graph.current_chart.update();
    }
  };

  return (
    <div className='field-details-wp field-details-wp--fluid field-details-wp--temperature'>
      <div className='field-details-wp__header'>
        <div className='field-details-wp__title-group'>
          <div className='field-details-wp__toggle'>
            <GraphToggle
              initialValue='temperature'
              onToggleClick={(toggle) => props.onToggleClick(toggle)}
              icon={props.icon}
              temperatureUnit={props.temperatureUnit}
            />
          </div>
          <span
            className={'field-details-wp__title ' + graphTitleColor}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {t('soil_temperature_graph_title')}
            {!dataInLoadingState && (
              <span
                className='spinner-border text-grey45'
                role='status'
                style={{ width: '15px', height: '15px' }}
              />
            )}
          </span>
        </div>

        {props.field_state === 'active' && (
          <div
            className='dropdown text-size-16 text-grey71 align-self-center'
            // Match the title's translate so the dropdown sits at the
            // same vertical position; raise above the chart frame so
            // the open menu isn't clipped.
            style={{
              transform: 'translateY(8px)',
              position: 'relative',
              zIndex: 10,
            }}
          >
            <div
              className='dropdown-toggle'
              data-toggle='dropdown'
              aria-haspopup='true'
              aria-expanded='false'
              style={{ cursor: 'pointer' }}
            >
              {dateFilterNames[daysBack.toString()]}
            </div>

            <div
              className='dropdown-menu dropdown-menu-right'
              aria-labelledby='dropdownMenuButton'
            >
              {DATE_FILTER_OPTIONS.map(({ days }) => (
                <a
                  key={`days-${days}`}
                  className='dropdown-item text-grey71'
                  href='#'
                  onClick={(e) => handleTimeRangeChange(e, days)}
                >
                  {dateFilterNames[days.toString()]}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className='field-details-wp__frame'>
        <div className='field-details-wp__chart'>
          <SensorGraph
            onRef={(ref) => setSensor_graph(ref)}
            hideNavigationButtons={true}
            units={userUnits}
            isLoading={false}
            shouldDrawBottom={false}
            shouldDrawInputs={false}
            shouldDrawResetButton={true}
            toolTipMode={'index'}
            distributionMode={'linear'}
            shouldDisplayZoom={false}
            shouldSpaceData={true}
            shouldDiplayUTC={false}
            shouldScaleGraph={false}
            field_state={props.field_state}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = function (state) {
  return {
    selectedField: state.distribution_data.selected_entities.field,
    selectedFarmingUnit: state.distribution_data.selected_entities.farming_unit,
    selectedDistribution:
      state.distribution_data.selected_entities.distribution,
    loadHistoryStatus: state.distribution_data.field_load_history,
    userData: state.authentication_data.user_data,
    field_state: state.distribution_data.field_state,
  };
};
const mapDispatchToProps = {};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(SoilTemperatureGraphContainer));
